export type FileAssociation = {
  id: string;
  objectTypeId: string;
  /** Null = type-only ("Category") association: about this type, no specific record. */
  objectRecordId: string | null;
  objectTypeName: string | null;
  objectRecordName: string | null;
};

export type ManagedFile = {
  id: string;
  name: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string | null;
  categoryId: string | null;
  /**
   * Forward-compat list shape for the Tag axis: 0 or 1 Tag ids, mirroring
   * `categoryId`. Multi-valued Tags are deferred — prefer reading this over
   * the scalar in new code.
   */
  labels: string[];
  sourceKind: string | null;
  sourceId: string | null;
  cui: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /**
   * For files promoted from a Relay email attachment, the source email's send
   * time (falls back to the message created_at). Null for direct uploads and
   * unresolved sources. Prefer `emailSentAt ?? createdAt` when rendering a date.
   */
  emailSentAt: string | null;
  associations: FileAssociation[];
};

export type FilesListResponse = {items: ManagedFile[]; total: number};

export type FileCategory = {
  id: string;
  name: string;
  sortOrder: number;
  deletedAt: string | null;
};

export type UploadUrlResult = {url: string; fileId: string; expiresIn: number};
export type ContentUrlResult = {url: string; expiresIn: number};

/**
 * Files at or above this size use S3 multipart upload (resumable, per-part)
 * instead of a single presigned PUT. The single-PUT path caps at 5 GB and has
 * no resume; multipart lifts both limits. Kept well under 5 GB so large-but-
 * not-huge files still benefit from per-part retry + progress.
 */
export const MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024; // 100 MB

/** Minimum confidence score for AI-powered file upload suggestions to be auto-applied or offered. */
export const SUGGESTION_CONFIDENCE_THRESHOLD = 0.8;

export type MultipartInitResult = {
  fileId: string;
  uploadId: string;
  partSize: number;
  partCount: number;
};

export type CompletedPart = {partNumber: number; etag: string};

export type CompleteMultipartInput = {
  uploadId: string;
  name: string;
  originalFilename: string;
  contentType: string;
  parts: CompletedPart[];
  association: {objectTypeId: string; objectRecordId: string | null};
  categoryId?: string;
  cui?: boolean;
};

export type CreateFileFromStagingInput = {
  fileId: string;
  name: string;
  originalFilename: string;
  contentType: string;
  categoryId?: string;
  associations?: Array<{objectTypeId: string; objectRecordId: string | null}>;
  cui?: boolean;
};

export type CreateFileFromRelayInput = {
  sourceEmailAttachmentId: string;
  name: string;
  categoryId?: string;
  associations?: Array<{objectTypeId: string; objectRecordId: string | null}>;
  cui?: boolean;
};

export type UpdateFileInput = {
  name?: string;
  categoryId?: string | null;
};

export type ListFilesParams = {
  associationRef?: string;
  categoryId?: string;
  search?: string;
  includeDeleted?: boolean;
  /** Restrict to files missing any association of a required object type. */
  missingRequiredAssociation?: boolean;
  /** Required object type ids — supplied by the caller; gates the filter above. */
  requiredObjectTypeIds?: string[];
  /** Max rows to fetch (backend default 100, max 500). Omit for the backend default. */
  limit?: number;
  /** Row offset for pagination (backend default 0). */
  offset?: number;
};
